import React, { useEffect, useMemo, useState } from "react";
import { Button, Dropdown, Select, Spin } from "antd";
import {
  format,
  intervalToDuration,
  setHours,
  setMinutes,
  isBefore,
  addMinutes,
} from "date-fns";
import { useNavigate, useParams } from "react-router-dom";
import { getPatient } from "../../apis/patient";
import {
  getPatientImmunizations,
  schedulePatientImmunizations,
  updatePatientImmunization,
} from "../../apis/patientImmunizations";
import { getVaccines, getVaccineSchedules } from "../../apis/vaccines";
import { getClinicVisitTypes, saveVisit } from "../../apis/visit";
import {
  ImmunizationStatus,
  PatientImmunization,
  Vaccine,
  VaccineSchedule,
} from "../../interfaces/vaccines";
import { useNotification } from "../../NotificationContext";
import Modal from "../../components/Modal";

const unwrapList = <T,>(res: any): T[] => {
  const data = res?.data ?? res;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  return [];
};

const STATUS_STYLES: Record<string, string> = {
  scheduled: "bg-blue-100 text-blue-800 border-blue-300",
  given: "bg-green-100 text-green-800 border-green-300",
  missed: "bg-red-100 text-red-800 border-red-300",
  deferred: "bg-yellow-100 text-yellow-800 border-yellow-300",
};

const STATUS_OPTIONS: { value: ImmunizationStatus; label: string }[] = [
  { value: "scheduled", label: "Scheduled" },
  { value: "given", label: "Given" },
  { value: "deferred", label: "Deferred" },
  { value: "missed", label: "Missed" },
];

/** Format overdue duration as "1y 2m 5d", omitting zero parts. */
const formatOverdueDuration = (
  recommendedDueDate?: string | null,
  overdueDays?: number | null
): string => {
  if (recommendedDueDate) {
    const start = new Date(recommendedDueDate);
    const end = new Date();
    if (!isNaN(start.getTime()) && end > start) {
      const { years = 0, months = 0, days = 0 } = intervalToDuration({
        start,
        end,
      });
      const parts: string[] = [];
      if (years > 0) parts.push(`${years}y`);
      if (months > 0) parts.push(`${months}m`);
      if (days > 0 || parts.length === 0) parts.push(`${days}d`);
      return parts.join(" ");
    }
  }

  if (overdueDays == null || overdueDays <= 0) return "";

  const years = Math.floor(overdueDays / 365);
  const months = Math.floor((overdueDays % 365) / 30);
  const days = overdueDays - years * 365 - months * 30;
  const parts: string[] = [];
  if (years > 0) parts.push(`${years}y`);
  if (months > 0) parts.push(`${months}m`);
  if (days > 0 || parts.length === 0) parts.push(`${days}d`);
  return parts.join(" ");
};

const PatientImmunizations: React.FC = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [patient, setPatient] = useState<any>(null);
  const [records, setRecords] = useState<PatientImmunization[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [loading, setLoading] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [vaccines, setVaccines] = useState<Vaccine[]>([]);
  const [schedules, setSchedules] = useState<VaccineSchedule[]>([]);
  const [selectedVaccineIds, setSelectedVaccineIds] = useState<number[]>([]);
  const [selectedScheduleIds, setSelectedScheduleIds] = useState<number[]>([]);
  const [scheduleDate, setScheduleDate] = useState(
    format(new Date(), "yyyy-MM-dd")
  );
  const [scheduleRemarks, setScheduleRemarks] = useState("");
  const [scheduling, setScheduling] = useState(false);
  const [statusModal, setStatusModal] = useState<{
    open: boolean;
    recordId: number | null;
    status: ImmunizationStatus | null;
    currentStatus: ImmunizationStatus | null;
    vaccineLabel: string;
  }>({
    open: false,
    recordId: null,
    status: null,
    currentStatus: null,
    vaccineLabel: "",
  });
  const [statusRemarks, setStatusRemarks] = useState("");
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [rescheduleModal, setRescheduleModal] = useState<{
    open: boolean;
    record: PatientImmunization | null;
  }>({ open: false, record: null });
  const [rescheduleDate, setRescheduleDate] = useState("");
  const [rescheduleRemarks, setRescheduleRemarks] = useState("");
  const [rescheduling, setRescheduling] = useState(false);
  const [creatingVisitId, setCreatingVisitId] = useState<number | null>(null);

  const pid = patientId ? Number(patientId) : null;

  useEffect(() => {
    if (!pid) return;
    fetchPatient();
    fetchRecords();
  }, [pid]);

  const fetchPatient = async () => {
    try {
      const res: any = await getPatient(pid!);
      setPatient(res?.data?.data ?? res?.data ?? res);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchRecords = async () => {
    if (!pid) return;
    setLoading(true);
    try {
      const res: any = await getPatientImmunizations({
        patient_id: pid,
        per_page: 500,
      });
      setRecords(unwrapList<PatientImmunization>(res));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const openScheduleModal = async () => {
    setScheduleDate(format(new Date(), "yyyy-MM-dd"));
    setScheduleRemarks("");
    setSelectedVaccineIds([]);
    setSelectedScheduleIds([]);
    setShowScheduleModal(true);
    try {
      const [vRes, sRes]: any[] = await Promise.all([
        getVaccines({ is_active: true, per_page: 1000 }),
        getVaccineSchedules({ per_page: 1000 }),
      ]);
      setVaccines(unwrapList(vRes));
      setSchedules(unwrapList(sRes));
    } catch (e) {
      console.error(e);
    }
  };

  const openChangeStatusModal = (record: PatientImmunization) => {
    const name = record.vaccine?.name || "Vaccine";
    const dose = record.vaccineSchedule?.doseNumber;
    setStatusModal({
      open: true,
      recordId: record.id,
      status: record.status,
      currentStatus: record.status,
      vaccineLabel: dose != null ? `${name} — Dose ${dose}` : name,
    });
    setStatusRemarks(record.remarks || "");
  };

  const closeStatusModal = () => {
    setStatusModal({
      open: false,
      recordId: null,
      status: null,
      currentStatus: null,
      vaccineLabel: "",
    });
    setStatusRemarks("");
  };

  const openRescheduleModal = (record: PatientImmunization) => {
    if (record.status === "given") {
      showNotification(
        "warning",
        "Given doses cannot be rescheduled",
        "",
        "topRight"
      );
      return;
    }
    setRescheduleModal({ open: true, record });
    setRescheduleDate(
      record.scheduledDate
        ? format(new Date(record.scheduledDate), "yyyy-MM-dd")
        : format(new Date(), "yyyy-MM-dd")
    );
    setRescheduleRemarks(record.remarks || "");
  };

  const closeRescheduleModal = () => {
    setRescheduleModal({ open: false, record: null });
    setRescheduleDate("");
    setRescheduleRemarks("");
  };

  const sortedVaccines = useMemo(() => {
    return [...vaccines].sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", undefined, {
        sensitivity: "base",
      })
    );
  }, [vaccines]);

  const filteredDoseOptions = useMemo(() => {
    if (selectedVaccineIds.length === 0) return [];
    return schedules
      .filter((s) => {
        const vaccineId = s.vaccineId ?? s.vaccine?.id;
        return vaccineId != null && selectedVaccineIds.includes(Number(vaccineId));
      })
      .sort((a, b) => {
        const nameA = a.vaccine?.name || "";
        const nameB = b.vaccine?.name || "";
        const byName = nameA.localeCompare(nameB, undefined, {
          sensitivity: "base",
        });
        if (byName !== 0) return byName;
        return (a.doseNumber || 0) - (b.doseNumber || 0);
      });
  }, [schedules, selectedVaccineIds]);

  const handleVaccineChange = (ids: number[]) => {
    setSelectedVaccineIds(ids);
    // Keep only doses that still belong to the selected vaccines
    setSelectedScheduleIds((prev) =>
      prev.filter((scheduleId) => {
        const schedule = schedules.find((s) => s.id === scheduleId);
        const vaccineId = schedule?.vaccineId ?? schedule?.vaccine?.id;
        return vaccineId != null && ids.includes(Number(vaccineId));
      })
    );
  };

  const filtered = useMemo(() => {
    if (statusFilter === "all") return records;
    if (statusFilter === "overdue") {
      return records.filter((r) => r.isOverdue);
    }
    return records.filter((r) => r.status === statusFilter);
  }, [records, statusFilter]);

  const counts = useMemo(() => {
    return {
      all: records.length,
      scheduled: records.filter((r) => r.status === "scheduled").length,
      given: records.filter((r) => r.status === "given").length,
      missed: records.filter((r) => r.status === "missed").length,
      deferred: records.filter((r) => r.status === "deferred").length,
      overdue: records.filter((r) => r.isOverdue).length,
    };
  }, [records]);

  const handleStatusChange = async () => {
    if (!statusModal.recordId || !statusModal.status) return;
    setUpdatingStatus(true);
    try {
      await updatePatientImmunization(statusModal.recordId, {
        status: statusModal.status,
        remarks: statusRemarks.trim() || null,
        ...(statusModal.status === "given"
          ? { vaccinationDate: format(new Date(), "yyyy-MM-dd") }
          : {}),
      });
      showNotification("success", "Immunization updated", "", "topRight");
      closeStatusModal();
      fetchRecords();
    } catch (e: any) {
      showNotification(
        "error",
        e?.response?.data?.message ||
          e?.response?.data?.details?.message ||
          "Failed to update",
        "",
        "topRight"
      );
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleReschedule = async () => {
    const record = rescheduleModal.record;
    if (!record) return;
    if (!rescheduleDate) {
      showNotification(
        "warning",
        "Please select a schedule date",
        "",
        "topRight"
      );
      return;
    }
    setRescheduling(true);
    try {
      await updatePatientImmunization(record.id, {
        scheduledDate: rescheduleDate,
        remarks: rescheduleRemarks.trim() || null,
        status: "scheduled",
      });
      showNotification(
        "success",
        record.visitId
          ? "Immunization and linked visit rescheduled"
          : "Immunization rescheduled",
        "",
        "topRight"
      );
      closeRescheduleModal();
      fetchRecords();
    } catch (e: any) {
      showNotification(
        "error",
        e?.response?.data?.message ||
          e?.response?.data?.details?.message ||
          "Failed to reschedule",
        "",
        "topRight"
      );
    } finally {
      setRescheduling(false);
    }
  };

  const handleCreateVisit = async (record: PatientImmunization) => {
    if (!pid) return;
    if (record.status === "given") {
      showNotification(
        "warning",
        "Cannot create visit for a given vaccination",
        "",
        "topRight"
      );
      return;
    }
    if (record.visitId) {
      showNotification(
        "info",
        "This immunization already has a linked visit",
        "",
        "topRight"
      );
      return;
    }
    setCreatingVisitId(record.id);
    try {
      const { doctorId, clinicVisitTypeId } = await resolveOpdVisitDefaults();
      if (!clinicVisitTypeId) {
        throw new Error("No OPD visit type found. Please configure one first.");
      }
      if (!doctorId) {
        throw new Error("Unable to determine doctor for the OPD visit.");
      }

      const dateStr = record.scheduledDate
        ? format(new Date(record.scheduledDate), "yyyy-MM-dd")
        : format(new Date(), "yyyy-MM-dd");

      const savedVisit: any = await saveVisit({
        patientId: pid,
        userId: doctorId,
        clinicVisitTypeId,
        date: buildVisitDateTime(dateStr),
        isInpatient: false,
        is_scheduled: true,
        meta: {
          isVaccination: true,
          vaccineScheduleIds: record.vaccineScheduleId
            ? [record.vaccineScheduleId]
            : [],
        },
      });

      if (!savedVisit?.id) {
        throw new Error("Failed to create OPD visit");
      }

      await updatePatientImmunization(record.id, {
        visitId: savedVisit.id,
      });

      showNotification(
        "success",
        "OPD visit created and linked to immunization",
        "",
        "topRight"
      );
      fetchRecords();
    } catch (e: any) {
      showNotification(
        "error",
        e?.response?.data?.message ||
          e?.response?.data?.details?.message ||
          e?.message ||
          "Failed to create visit",
        "",
        "topRight"
      );
    } finally {
      setCreatingVisitId(null);
    }
  };

  const actionMenuItems = (record: PatientImmunization) => {
    const items: any[] = [
      {
        key: "change-status",
        label: (
          <span onClick={() => openChangeStatusModal(record)}>
            Change Status
          </span>
        ),
      },
    ];
    if (record.status !== "given") {
      items.push({
        key: "reschedule",
        label: (
          <span onClick={() => openRescheduleModal(record)}>Reschedule</span>
        ),
      });
    }
    if (!record.visitId && record.status !== "given") {
      items.push({
        key: "create-visit",
        disabled: creatingVisitId === record.id,
        label: (
          <span onClick={() => handleCreateVisit(record)}>
            {creatingVisitId === record.id ? "Creating Visit..." : "Create Visit"}
          </span>
        ),
      });
    }
    return items;
  };

  const resolveOpdVisitDefaults = async () => {
    const userRaw = window.localStorage.getItem("user");
    const userObj = userRaw ? JSON.parse(userRaw) : null;
    const doctorId =
      userObj?.roles?.[0]?.name === "doctor"
        ? userObj.id
        : userObj?.id || null;

    const typesRes: any = await getClinicVisitTypes("opd");
    const types = Array.isArray(typesRes?.data)
      ? typesRes.data
      : unwrapList<any>(typesRes);
    const defaultTypeId = userObj?.defaultClinicVisitTypeId;
    const preferred =
      types.find((t: any) => t.id === defaultTypeId) ||
      types.find(
        (t: any) =>
          String(t.visitType?.type || t.type || "").toLowerCase() === "opd"
      ) ||
      types[0];

    return {
      doctorId,
      clinicVisitTypeId: preferred?.id ?? null,
    };
  };

  const buildVisitDateTime = (dateStr: string) => {
    // Prefer 10:00 on the schedule date; if that is already past, use now + 30 mins
    let visitAt = setMinutes(setHours(new Date(`${dateStr}T00:00:00`), 10), 0);
    if (isBefore(visitAt, new Date())) {
      visitAt = addMinutes(new Date(), 30);
    }
    return visitAt.toISOString();
  };

  const handleSchedule = async () => {
    if (!pid) return;
    if (selectedScheduleIds.length === 0 && selectedVaccineIds.length === 0) {
      showNotification(
        "warning",
        "Select at least one vaccine or dose",
        "",
        "topRight"
      );
      return;
    }
    if (!scheduleDate) {
      showNotification(
        "warning",
        "Please select a schedule date",
        "",
        "topRight"
      );
      return;
    }

    const scheduleIdsForMeta =
      selectedScheduleIds.length > 0
        ? selectedScheduleIds
        : filteredDoseOptions.map((s) => s.id!).filter(Boolean);

    const existingForSelection = records.filter((r) =>
      scheduleIdsForMeta.includes(r.vaccineScheduleId)
    );
    const alreadyGiven = existingForSelection.filter((r) => r.status === "given");
    if (alreadyGiven.length > 0) {
      const labels = alreadyGiven.map((r) => {
        const name = r.vaccine?.name || r.vaccineSchedule?.vaccine?.name || "Vaccine";
        const dose = r.vaccineSchedule?.doseNumber;
        return dose != null ? `${name} dose ${dose}` : name;
      });
      showNotification(
        "error",
        `${labels.join(", ")} already given and cannot be scheduled again.`,
        "",
        "topRight"
      );
      return;
    }

    setScheduling(true);
    try {
      const { doctorId, clinicVisitTypeId } = await resolveOpdVisitDefaults();
      if (!clinicVisitTypeId) {
        throw new Error("No OPD visit type found. Please configure one first.");
      }
      if (!doctorId) {
        throw new Error("Unable to determine doctor for the OPD visit.");
      }

      const reschedulable = existingForSelection.filter((r) =>
        ["scheduled", "deferred", "missed"].includes(r.status)
      );
      const existingVisitId =
        reschedulable.find((r) => r.visitId)?.visitId ?? null;
      const isDateOnlyUpdate = !!existingVisitId;

      let visitId: number | null | undefined = existingVisitId;

      // Always create an OPD visit when none is linked yet (new schedule or
      // orphaned immunizations from an earlier attempt without a visit).
      if (!visitId) {
        const savedVisit: any = await saveVisit({
          patientId: pid,
          userId: doctorId,
          clinicVisitTypeId,
          date: buildVisitDateTime(scheduleDate),
          isInpatient: false,
          is_scheduled: true,
          meta: {
            isVaccination: true,
            vaccineScheduleIds: scheduleIdsForMeta,
            ...(selectedVaccineIds.length > 0
              ? { vaccineIds: selectedVaccineIds }
              : {}),
          },
        });
        visitId = savedVisit?.id;
        if (!visitId) {
          throw new Error("Failed to create OPD visit for vaccination");
        }
      }

      await schedulePatientImmunizations({
        patientId: pid,
        visitId,
        vaccineIds:
          selectedVaccineIds.length > 0 ? selectedVaccineIds : undefined,
        vaccineScheduleIds:
          selectedScheduleIds.length > 0 ? selectedScheduleIds : undefined,
        scheduledDate: scheduleDate,
        remarks: scheduleRemarks.trim() || null,
      });

      showNotification(
        "success",
        isDateOnlyUpdate
          ? "Vaccination schedule and visit date updated"
          : "Vaccination scheduled and OPD visit created",
        "",
        "topRight"
      );
      setShowScheduleModal(false);
      setSelectedVaccineIds([]);
      setSelectedScheduleIds([]);
      setScheduleDate(format(new Date(), "yyyy-MM-dd"));
      setScheduleRemarks("");
      fetchRecords();
    } catch (e: any) {
      showNotification(
        "error",
        e?.response?.data?.message ||
          e?.response?.data?.details?.message ||
          e?.message ||
          "Failed to schedule",
        "",
        "topRight"
      );
    } finally {
      setScheduling(false);
    }
  };

  const doseLabel = (r: PatientImmunization) => {
    const dose = r.vaccineSchedule?.doseNumber;
    const booster = r.vaccineSchedule?.isBooster ? " (Booster)" : "";
    return dose != null ? `Dose ${dose}${booster}` : "-";
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mb-2 text-sm text-indigo-600 hover:text-indigo-800"
          >
            ← Back
          </button>
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            Immunization Record
          </h1>
          <p className="mt-1 text-sm text-gray-700 capitalize">
            {patient
              ? `${patient.firstName || ""} ${patient.lastName || ""}`.trim()
              : `Patient #${patientId}`}
          </p>
        </div>
        <button
          type="button"
          onClick={openScheduleModal}
          className="px-3 py-2 text-sm font-semibold text-white rounded-md shadow-sm bg-primary hover:bg-hover"
        >
          Schedule Vaccines
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mt-4">
        {(
          [
            ["all", "All"],
            ["scheduled", "Scheduled"],
            ["given", "Given"],
            ["missed", "Missed"],
            ["deferred", "Deferred"],
            ["overdue", "Overdue"],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setStatusFilter(key)}
            className={`px-3 py-1.5 text-xs font-medium rounded-full border ${
              statusFilter === key
                ? "bg-indigo-600 text-white border-indigo-600"
                : "bg-white text-gray-700 border-gray-300"
            }`}
          >
            {label} ({(counts as any)[key] ?? 0})
          </button>
        ))}
      </div>

      <div className="mt-6 overflow-x-auto -mx-4 ring-1 ring-gray-300 sm:mx-0 sm:rounded-lg">
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-gray-50">
            <tr>
              <th className="py-3.5 pr-3 pl-4 text-sm font-semibold text-left text-gray-900 sm:pl-6">
                Vaccine
              </th>
              <th className="px-3 py-3.5 text-sm font-semibold text-left text-gray-900">
                Dose
              </th>
              <th className="px-3 py-3.5 text-sm font-semibold text-left text-gray-900">
                Schedule Date
              </th>
              <th className="px-3 py-3.5 text-sm font-semibold text-left text-gray-900">
                Recommended Due
              </th>
              <th className="px-3 py-3.5 text-sm font-semibold text-left text-gray-900">
                Given Date
              </th>
              <th className="px-3 py-3.5 text-sm font-semibold text-left text-gray-900">
                Status
              </th>
              <th className="px-3 py-3.5 text-sm font-semibold text-left text-gray-900">
                Remarks
              </th>
              <th className="px-3 py-3.5 text-sm font-semibold text-left text-gray-900">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={8} className="px-6 py-8 text-sm text-center text-gray-500">
                  Loading...
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-6 py-8 text-sm text-center text-gray-500">
                  No immunization records for this filter.
                </td>
              </tr>
            ) : (
              filtered.map((r) => (
                <tr key={r.id}>
                  <td className="py-4 pr-3 pl-4 text-sm font-medium text-gray-900 sm:pl-6">
                    {r.vaccine?.name || "-"}
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-700">
                    {doseLabel(r)}
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-500">
                    {r.scheduledDate
                      ? format(new Date(r.scheduledDate), "dd MMM yyyy")
                      : "-"}
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-500">
                    {r.recommendedDueDate
                      ? format(new Date(r.recommendedDueDate), "dd MMM yyyy")
                      : r.dueDate
                        ? format(new Date(r.dueDate), "dd MMM yyyy")
                        : "-"}
                    {r.isOverdue && (
                      <span className="ml-2 text-xs font-medium text-red-600">
                        Overdue
                        {(() => {
                          const duration = formatOverdueDuration(
                            r.recommendedDueDate || r.dueDate,
                            r.overdueDays
                          );
                          return duration ? ` (${duration})` : "";
                        })()}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-500">
                    {r.vaccinationDate
                      ? format(new Date(r.vaccinationDate), "dd MMM yyyy")
                      : "-"}
                  </td>
                  <td className="px-3 py-4 text-sm">
                    <span
                      className={`inline-flex px-2 py-0.5 text-xs font-medium rounded border capitalize ${
                        STATUS_STYLES[r.status] || STATUS_STYLES.scheduled
                      }`}
                    >
                      {r.status}
                    </span>
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-500 max-w-[200px]">
                    <span className="line-clamp-2" title={r.remarks || ""}>
                      {r.remarks || "-"}
                    </span>
                  </td>
                  <td className="relative py-4 pr-4 pl-3 text-sm font-medium text-left whitespace-nowrap sm:pr-6">
                    <Dropdown
                      menu={{ items: actionMenuItems(r) }}
                      placement="bottomLeft"
                      disabled={creatingVisitId === r.id}
                    >
                      <Button loading={creatingVisitId === r.id}>
                        {creatingVisitId === r.id ? "Please wait..." : "Actions"}
                      </Button>
                    </Dropdown>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showScheduleModal && (
        <Modal
          title="Schedule Vaccines"
          onClose={() => setShowScheduleModal(false)}
          width="max-w-xl"
          modalContent={
            <div className="p-4 space-y-4">
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Vaccines
                </label>
                <Select
                  mode="multiple"
                  allowClear
                  style={{ width: "100%" }}
                  placeholder="Select vaccines"
                  value={selectedVaccineIds}
                  onChange={handleVaccineChange}
                  optionFilterProp="label"
                  options={sortedVaccines.map((v) => ({
                    value: v.id,
                    label: v.shortName
                      ? `${v.name} (${v.shortName})`
                      : v.name,
                  }))}
                />
              </div>
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Doses
                </label>
                <Select
                  mode="multiple"
                  allowClear
                  style={{ width: "100%" }}
                  placeholder={
                    selectedVaccineIds.length === 0
                      ? "Select a vaccine first"
                      : "Select doses"
                  }
                  disabled={selectedVaccineIds.length === 0}
                  value={selectedScheduleIds}
                  onChange={setSelectedScheduleIds}
                  optionFilterProp="label"
                  options={filteredDoseOptions.map((s) => ({
                    value: s.id!,
                    label: `${s.vaccine?.name || "Vaccine"} — Dose ${s.doseNumber}${
                      s.isBooster ? " (Booster)" : ""
                    }`,
                  }))}
                />
                {selectedVaccineIds.length === 0 && (
                  <p className="mt-1 text-xs text-gray-500">
                    Select one or more vaccines to see their doses.
                  </p>
                )}
              </div>
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Schedule Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  className="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                />
                <p className="mt-1 text-xs text-gray-500">
                  An OPD vaccination visit will be created for this date.
                </p>
              </div>
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Remarks
                </label>
                <textarea
                  rows={3}
                  className="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                  placeholder="Optional remarks"
                  value={scheduleRemarks}
                  onChange={(e) => setScheduleRemarks(e.target.value)}
                />
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => setShowScheduleModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={scheduling}
                  onClick={handleSchedule}
                  className="px-4 py-2 text-sm font-semibold text-white rounded-md bg-primary hover:bg-hover disabled:opacity-50"
                >
                  {scheduling ? "Scheduling..." : "Schedule"}
                </button>
              </div>
            </div>
          }
        />
      )}

      {statusModal.open && (
        <Modal
          title="Change Status"
          onClose={() => {
            if (!updatingStatus) closeStatusModal();
          }}
          width="max-w-md"
          modalContent={
            <Spin spinning={updatingStatus} tip="Updating status...">
              <div className="p-4 space-y-4">
                {statusModal.vaccineLabel && (
                  <p className="text-sm text-gray-600">
                    {statusModal.vaccineLabel}
                  </p>
                )}
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    Status <span className="text-red-500">*</span>
                  </label>
                  <Select
                    style={{ width: "100%" }}
                    value={statusModal.status || undefined}
                    disabled={updatingStatus}
                    onChange={(v) =>
                      setStatusModal((prev) => ({
                        ...prev,
                        status: v as ImmunizationStatus,
                      }))
                    }
                    options={STATUS_OPTIONS}
                  />
                </div>
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    Remarks
                  </label>
                  <textarea
                    rows={3}
                    disabled={updatingStatus}
                    className="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm disabled:bg-gray-50 disabled:opacity-70"
                    placeholder="Optional remarks for this status change"
                    value={statusRemarks}
                    onChange={(e) => setStatusRemarks(e.target.value)}
                  />
                </div>
                <div className="flex gap-3 justify-end">
                  <button
                    type="button"
                    disabled={updatingStatus}
                    onClick={closeStatusModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={updatingStatus || !statusModal.status}
                    onClick={handleStatusChange}
                    className="inline-flex gap-2 items-center px-4 py-2 text-sm font-semibold text-white rounded-md bg-primary hover:bg-hover disabled:opacity-50"
                  >
                    {updatingStatus ? "Updating..." : "Update"}
                  </button>
                </div>
              </div>
            </Spin>
          }
        />
      )}

      {rescheduleModal.open && rescheduleModal.record && (
        <Modal
          title="Reschedule Immunization"
          onClose={() => {
            if (!rescheduling) closeRescheduleModal();
          }}
          width="max-w-md"
          modalContent={
            <Spin spinning={rescheduling} tip="Rescheduling...">
              <div className="p-4 space-y-4">
                <p className="text-sm text-gray-600">
                  {rescheduleModal.record.vaccine?.name || "Vaccine"}
                  {rescheduleModal.record.vaccineSchedule?.doseNumber != null
                    ? ` — Dose ${rescheduleModal.record.vaccineSchedule.doseNumber}`
                    : ""}
                  {rescheduleModal.record.visitId
                    ? ". Linked visit date will also be updated."
                    : ""}
                </p>
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    Schedule Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    disabled={rescheduling}
                    className="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm disabled:bg-gray-50 disabled:opacity-70"
                    value={rescheduleDate}
                    onChange={(e) => setRescheduleDate(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    Remarks
                  </label>
                  <textarea
                    rows={3}
                    disabled={rescheduling}
                    className="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm disabled:bg-gray-50 disabled:opacity-70"
                    placeholder="Optional remarks"
                    value={rescheduleRemarks}
                    onChange={(e) => setRescheduleRemarks(e.target.value)}
                  />
                </div>
                <div className="flex gap-3 justify-end">
                  <button
                    type="button"
                    disabled={rescheduling}
                    onClick={closeRescheduleModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={rescheduling}
                    onClick={handleReschedule}
                    className="inline-flex gap-2 items-center px-4 py-2 text-sm font-semibold text-white rounded-md bg-primary hover:bg-hover disabled:opacity-50"
                  >
                    {rescheduling ? "Saving..." : "Reschedule"}
                  </button>
                </div>
              </div>
            </Spin>
          }
        />
      )}

      {creatingVisitId != null && (
        <div className="flex fixed inset-0 z-50 justify-center items-center bg-black/30">
          <div className="flex flex-col gap-3 items-center px-8 py-6 bg-white rounded-lg shadow-lg">
            <Spin size="large" />
            <p className="text-sm font-medium text-gray-700">Creating visit...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientImmunizations;
